import { useEffect, useState } from "react";

import api from "../api/client";
import InboundChannels from "./InboundChannels";
import WebhooksCard from "../components/WebhooksCard";

/**
 * Slack & Teams, all of it, on one screen.
 *
 * REPORTED, after several goes at getting Teams working: "We have a sub-menu
 * item that says Slack/Teams. Either put everything regarding slack and teams
 * there, make it easy, make it gorgeous, or eliminate it and re-work."
 *
 * The mess was structural. Chat lived in two menu entries sitting right next
 * to each other — "Alert destinations" and "Slack / Teams" — doing OPPOSITE
 * things, and the vendors' own vocabulary makes them read as each other's
 * definition:
 *
 *   Teams calls the thing that sends OUT of Teams an "Outgoing Webhook"
 *      -> that one feeds AeroGauge. It is INBOUND to us.
 *   Teams calls the thing that receives INTO Teams a workflow that
 *   "receives a webhook request"
 *      -> that one is fed BY AeroGauge. It is OUTBOUND from us.
 *
 * So somebody holding a URL has no way to know which screen wants it, and the
 * first person to try went to the wrong one and found no box to paste into.
 *
 * One screen now, split by DIRECTION rather than by mechanism, with the
 * direction stated as a sentence at the top of each half. The old routes still
 * resolve — bookmarks, and the generated docs point at them.
 */

type Direction = "out" | "in";

export default function ChatIntegration() {
  const [tab, setTab] = useState<Direction>("out");
  // Counts on the tabs, so the screen says what is already set up rather than
  // making the operator click both halves to find out.
  const [counts, setCounts] = useState<{ out: number | null; in: number | null }>({
    out: null,
    in: null,
  });

  useEffect(() => {
    api
      .get("/api/v1/webhooks")
      .then((r) => setCounts((c) => ({ ...c, out: (r.data || []).length })))
      .catch(() => {});
    api
      .get("/api/v1/inbound/channels")
      .then((r) => setCounts((c) => ({ ...c, in: (r.data || []).length })))
      .catch(() => {});
  }, []);

  const cards: {
    key: Direction;
    title: string;
    flow: string;
    body: string;
    vendor: string;
  }[] = [
    {
      key: "out",
      title: "Alerts into a channel",
      flow: "AeroGauge → Slack / Teams",
      body: "A device goes down and the channel hears about it. Also SLA breaches, and PagerDuty if you page.",
      vendor: "In Teams you create a Workflow. In Slack, an Incoming Webhook.",
    },
    {
      key: "in",
      title: "Tickets out of a channel",
      flow: "Slack / Teams → AeroGauge",
      body: "Someone types in chat and a ticket appears, on the customer you have mapped that channel to.",
      vendor: "In Teams you create an Outgoing Webhook. In Slack, a slash command.",
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Slack &amp; Teams</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Everything that connects AeroGauge to a chat tool. Pick the direction you want.
        </p>
      </div>

      {/* Direction first. Both vendors name these features after the opposite
          end of the connection, so the picture has to come before the form. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {cards.map((c) => {
          const active = tab === c.key;
          const n = counts[c.key];
          return (
            <button
              key={c.key}
              onClick={() => setTab(c.key)}
              className={`text-left rounded-xl border p-4 transition-colors ${
                active
                  ? "border-brand bg-brand/5 dark:bg-brand/10 ring-1 ring-brand"
                  : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 dark:text-gray-100">{c.title}</div>
                  <div className="mt-0.5 font-mono text-[11px] text-brand dark:text-brand-light">
                    {c.flow}
                  </div>
                </div>
                {n !== null && (
                  <span
                    className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full ${
                      n > 0
                        ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {n > 0 ? `${n} set up` : "none yet"}
                  </span>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">{c.body}</p>
              <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">{c.vendor}</p>
            </button>
          );
        })}
      </div>

      {tab === "out" ? <WebhooksCard /> : <InboundChannels embedded />}
    </div>
  );
}
